import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot, Calendar, Hash, Armchair, Clock, Building2 } from 'lucide-react';
import { TRANSPORT_TYPES } from './constants';
import { format } from 'date-fns';

const iconMap = {
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot
};

// Plane details form
const PlaneForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.airline')}</Label>
        <div className="relative mt-1">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.airline || ''}
            onChange={(e) => onChange({ ...details, airline: e.target.value })}
            placeholder="Ex: LATAM, GOL"
            className="pl-10"
          />
        </div>
      </div>
      <div>
        <Label>{t('myTrip.transport.flightNumber')}</Label>
        <div className="relative mt-1">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.flight_number || ''}
            onChange={(e) => onChange({ ...details, flight_number: e.target.value })}
            placeholder="Ex: LA3456"
            className="pl-10"
          />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.departure')}</Label>
        <div className="relative mt-1">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="datetime-local"
            value={details?.departure_datetime ? format(new Date(details.departure_datetime), "yyyy-MM-dd'T'HH:mm") : ''}
            onChange={(e) => onChange({ ...details, departure_datetime: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="pl-10"
          />
        </div>
      </div>
      <div>
        <Label>{t('myTrip.transport.seat')}</Label>
        <div className="relative mt-1">
          <Armchair className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.seat || ''}
            onChange={(e) => onChange({ ...details, seat: e.target.value })}
            placeholder="Ex: 12A"
            className="pl-10"
          />
        </div>
      </div>
    </div>
    <div>
      <Label>{t('myTrip.transport.bookingCode')}</Label>
      <Input
        value={details?.booking_code || ''}
        onChange={(e) => onChange({ ...details, booking_code: e.target.value })}
        placeholder="Ex: ABC123"
        className="mt-1"
      />
    </div>
  </div>
);

// Bus details form
const BusForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.company')}</Label>
        <div className="relative mt-1">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.company || ''}
            onChange={(e) => onChange({ ...details, company: e.target.value })}
            placeholder="Ex: Cometa, 1001"
            className="pl-10"
          />
        </div>
      </div>
      <div>
        <Label>{t('myTrip.transport.ticketNumber')}</Label>
        <div className="relative mt-1">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.ticket_number || ''}
            onChange={(e) => onChange({ ...details, ticket_number: e.target.value })}
            placeholder="Número do bilhete"
            className="pl-10"
          />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.departure')}</Label>
        <div className="relative mt-1">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="datetime-local"
            value={details?.departure_datetime ? format(new Date(details.departure_datetime), "yyyy-MM-dd'T'HH:mm") : ''}
            onChange={(e) => onChange({ ...details, departure_datetime: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="pl-10"
          />
        </div>
      </div>
      <div>
        <Label>{t('myTrip.transport.seat')}</Label>
        <div className="relative mt-1">
          <Armchair className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={details?.seat || ''}
            onChange={(e) => onChange({ ...details, seat: e.target.value })}
            placeholder="Ex: 15"
            className="pl-10"
          />
        </div>
      </div>
    </div>
    <div>
      <Label>{t('myTrip.transport.platform')}</Label>
      <Input
        value={details?.platform || ''}
        onChange={(e) => onChange({ ...details, platform: e.target.value })}
        placeholder="Ex: Plataforma 5"
        className="mt-1"
      />
    </div>
  </div>
);

// Car details form
const CarForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.carModel')}</Label>
        <Input
          value={details?.car_model || ''}
          onChange={(e) => onChange({ ...details, car_model: e.target.value })}
          placeholder="Ex: Honda Civic"
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.carColor')}</Label>
        <Input
          value={details?.car_color || ''}
          onChange={(e) => onChange({ ...details, car_color: e.target.value })}
          placeholder="Ex: Prata"
          className="mt-1"
        />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.licensePlate')}</Label>
        <Input
          value={details?.license_plate || ''}
          onChange={(e) => onChange({ ...details, license_plate: e.target.value })}
          placeholder="Ex: ABC-1234"
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.availableSeats')}</Label>
        <Input
          type="number"
          min="0"
          value={details?.available_seats || ''}
          onChange={(e) => onChange({ ...details, available_seats: parseInt(e.target.value) || 0 })}
          placeholder="Vagas disponíveis"
          className="mt-1"
        />
      </div>
    </div>
  </div>
);

// Train details form
const TrainForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.company')}</Label>
        <Input
          value={details?.company || ''}
          onChange={(e) => onChange({ ...details, company: e.target.value })}
          placeholder="Ex: CPTM"
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.trainNumber')}</Label>
        <Input
          value={details?.train_number || ''}
          onChange={(e) => onChange({ ...details, train_number: e.target.value })}
          placeholder="Número do trem"
          className="mt-1"
        />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.departure')}</Label>
        <Input
          type="datetime-local"
          value={details?.departure_datetime ? format(new Date(details.departure_datetime), "yyyy-MM-dd'T'HH:mm") : ''}
          onChange={(e) => onChange({ ...details, departure_datetime: e.target.value ? new Date(e.target.value).toISOString() : null })}
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.wagon')}</Label>
        <Input
          value={details?.wagon || ''}
          onChange={(e) => onChange({ ...details, wagon: e.target.value })}
          placeholder="Ex: Vagão 3"
          className="mt-1"
        />
      </div>
    </div>
  </div>
);

// Boat details form
const BoatForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.company')}</Label>
        <Input
          value={details?.company || ''}
          onChange={(e) => onChange({ ...details, company: e.target.value })}
          placeholder="Ex: CCR Barcas"
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.vesselName')}</Label>
        <Input
          value={details?.vessel_name || ''}
          onChange={(e) => onChange({ ...details, vessel_name: e.target.value })}
          placeholder="Nome da embarcação"
          className="mt-1"
        />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('myTrip.transport.departure')}</Label>
        <Input
          type="datetime-local"
          value={details?.departure_datetime ? format(new Date(details.departure_datetime), "yyyy-MM-dd'T'HH:mm") : ''}
          onChange={(e) => onChange({ ...details, departure_datetime: e.target.value ? new Date(e.target.value).toISOString() : null })}
          className="mt-1"
        />
      </div>
      <div>
        <Label>{t('myTrip.transport.cabin')}</Label>
        <Input
          value={details?.cabin || ''}
          onChange={(e) => onChange({ ...details, cabin: e.target.value })}
          placeholder="Ex: Cabine 101"
          className="mt-1"
        />
      </div>
    </div>
  </div>
);

// Other transport details form
const OtherForm = ({ details, onChange, t }) => (
  <div className="space-y-4">
    <div>
      <Label>{t('myTrip.transport.description')}</Label>
      <Input
        value={details?.description || ''}
        onChange={(e) => onChange({ ...details, description: e.target.value })}
        placeholder="Descreva o meio de transporte"
        className="mt-1"
      />
    </div>
    <div>
      <Label>{t('myTrip.transport.details')}</Label>
      <Input
        value={details?.details || ''}
        onChange={(e) => onChange({ ...details, details: e.target.value })}
        placeholder="Detalhes adicionais"
        className="mt-1"
      />
    </div>
  </div>
);

const TransportDetailsModal = ({ 
  open, 
  onOpenChange, 
  transportType,
  details,
  onSave 
}) => {
  const { t, language } = useLanguage();
  const [localDetails, setLocalDetails] = useState(details || {});
  
  // Sync state when modal opens or details change
  useEffect(() => {
    if (open) {
      setLocalDetails(details || {});
    }
  }, [open, details]);
  
  const transportConfig = TRANSPORT_TYPES[transportType];
  const IconComponent = transportConfig ? iconMap[transportConfig.icon] : CircleDot;

  const handleSave = () => {
    onSave(localDetails);
    onOpenChange(false);
  };

  const renderForm = () => {
    switch (transportType) {
      case 'plane':
        return <PlaneForm details={localDetails} onChange={setLocalDetails} t={t} />;
      case 'bus':
        return <BusForm details={localDetails} onChange={setLocalDetails} t={t} />;
      case 'car':
        return <CarForm details={localDetails} onChange={setLocalDetails} t={t} />;
      case 'train':
        return <TrainForm details={localDetails} onChange={setLocalDetails} t={t} />;
      case 'boat':
        return <BoatForm details={localDetails} onChange={setLocalDetails} t={t} />;
      case 'van':
      case 'motorcycle':
        return <CarForm details={localDetails} onChange={setLocalDetails} t={t} />;
      default:
        return <OtherForm details={localDetails} onChange={setLocalDetails} t={t} />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] !p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <IconComponent className="w-5 h-5 text-orange-500" />
            {t('myTrip.transport.detailsTitle')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {transportConfig?.label[language] || transportConfig?.label['pt-BR']}
          </DialogDescription>
        </DialogHeader>
        
        <div className="px-4 sm:px-6 pb-4 overflow-y-auto">
          {renderForm()}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-2 border-t">
          <Button
            variant="outline"
            className="flex-1 rounded-full order-2 sm:order-1"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 order-1 sm:order-2"
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransportDetailsModal;
